import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { CalendarJob } from "./job-chip";

// Status tint for the left-rule inside the popover rows — same palette as JobChip.
type ChipTint = "accent" | "success" | "muted";
const JOB_STATUS_TINT: Record<string, ChipTint> = {
  new: "muted",
  scheduled: "accent",
  in_progress: "accent",
  completed: "success",
  canceled: "muted",
};
const BORDER: Record<ChipTint, string> = {
  accent: "border-l-[color:var(--color-accent)]",
  success: "border-l-[color:var(--color-success)]",
  muted: "border-l-[color:var(--color-text-lo)]",
};

interface DayOverflowPopoverProps {
  jobs: CalendarJob[];
  onOpenJob: (id: string) => void;
}

/**
 * "+N more" trigger for month-grid day cells. Clicking opens a popover that
 * lists all overflow jobs with their crew and job-number metadata — same
 * information density as the Day view row, so the operator never needs to
 * switch views just to see what overflowed (ADR-0019 §"progression entities").
 */
export function DayOverflowPopover({
  jobs,
  onOpenJob,
}: DayOverflowPopoverProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="block w-full px-1.5 text-left text-xs text-[color:var(--color-text-lo)] hover:text-[color:var(--color-text-md)] transition-colors"
        >
          +{jobs.length} more
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-1.5" align="start" sideOffset={4}>
        <ul role="list" className="flex flex-col gap-0.5">
          {jobs.map((job) => {
            const tint = JOB_STATUS_TINT[job.status] ?? "muted";
            const meta = [job.crew, job.jobNumber].filter(Boolean);
            return (
              <li key={job.id}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onOpenJob(job.id);
                  }}
                  className={cn(
                    "flex w-full flex-col gap-0.5 rounded-[var(--radius-sm)]",
                    "border-l-2 bg-[color:var(--color-surface)] px-2 py-1",
                    "text-left hover:bg-[color:var(--color-elevated)]",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]",
                    "transition-colors",
                    BORDER[tint],
                    job.canceled && "opacity-60",
                  )}
                >
                  <span
                    className={cn(
                      "truncate text-xs font-medium text-[color:var(--color-text-hi)]",
                      job.canceled && "line-through",
                    )}
                  >
                    {job.label}
                  </span>
                  {meta.length > 0 && (
                    <span className="flex items-center gap-2 text-xs text-[color:var(--color-text-lo)]">
                      {meta.map((m) => (
                        <span key={m}>{m}</span>
                      ))}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
